import { Field, Formik } from 'formik';
import {
	CerebroDrawer,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroHeading,
	CerebroPrimaryButton,
} from '../../shared/components';
import { LoanApplicationTask } from '../../shared/models';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { useRef } from 'react';
import { useApi } from '../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys } from '../../shared/hooks';
import { getApiResponseData } from '../../shared/utils/axios';

interface UploadExampleTemplateDrawerProps {
	open?: boolean;
	onClose: () => void;
	document: LoanApplicationTask;
}

export const UploadExampleTemplateDrawer: React.FC<UploadExampleTemplateDrawerProps> = ({
	open = false,
	onClose,
	document: requiredDocument,
}) => {
	const api = useApi();
	const { guid } = useParams<'guid'>();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const ref = useRef<HTMLDivElement>(null);

	const uploadMutation = useMutation(
		async ({ values }: ExampleTaskMutationValues) => {
			let filesUpdate: LoanApplicationTask | null = null;
			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<LoanApplicationTask>(
					api.post(
						CerebroApiEndpoints.RequiredDocuments.UploadExample(guid, requiredDocument.guid),
						formData,
						{
							headers: { 'Content-Type': 'multipart/form-data' },
						}
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				filesUpdate = data;
			}

			return { filesUpdate };
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid));
				snackbar.enqueueSnackbar('Uploaded example template file.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading size="h2" component="h3" gutterBottom>
				Upload Example Template File
			</CerebroHeading>
			<Formik
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(uploadMutation.mutate)}
				initialValues={{
					uploads: [] as File[],
				}}
			>
				{({ isSubmitting, handleSubmit }) => (
					<CerebroForm
						flatIndicator
						encType="multipart/form-data"
						onSubmit={handleSubmit}
						sx={{ marginTop: 3, paddingRight: 3 }}
						scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
					>
						<Field
							as={CerebroFileDragAndDrop}
							name="uploads"
							sx={{ marginY: 3, width: '100%' }}
							onlyPdf={false}
							multiple={true}
						/>

						<CerebroPrimaryButton loading={isSubmitting} type="submit" sx={{ marginTop: 3 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type ExampleTaskMutationValues = FormikUtils.MutateOnSubmitVariables<ExampleTaskValues>;
interface ExampleTaskValues {
	uploads: File[];
}
